import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';

interface SettingsModel {
  SendMethod: 'VM_DIRECT_API';
  TimeoutSeconds: number;
  RetryCount: number;
  TlsVerify: number;
}

@Component({
  selector: 'app-settings-communication',
  imports: [FormsModule],
  template: `
    <section class="stack">
      <section class="card">
        <header class="head">
          <div>
            <h1>Communication</h1>
            <p>Communication behavior settings. VM login URL is configured per property in Property Administration.</p>
          </div>
        </header>

        @if (loading()) { <div>Loading…</div> }
        @if (error()) { <div class="error">{{ error() }}</div> }
        @if (ok()) { <div class="ok">{{ ok() }}</div> }

        @if (model) {
          <div class="grid">
            <label>Send Method<input [ngModel]="model.SendMethod" disabled></label>
            <label>Timeout Seconds<input type="number" [(ngModel)]="model.TimeoutSeconds"></label>
            <label>Retry Count<input type="number" [(ngModel)]="model.RetryCount"></label>
            <label class="row"><input type="checkbox" [ngModel]="model.TlsVerify===1" (ngModelChange)="onTlsChanged($event)"> TLS Verify</label>
          </div>
          <div class="row-actions">
            <button type="button" (click)="save()" [disabled]="saving()">{{ saving() ? 'Saving…' : 'Save Settings' }}</button>
            <button type="button" class="secondary" (click)="applyDbUpdates()" [disabled]="saving()">Apply DB Updates</button>
          </div>
        }
      </section>

      <section class="card">
        <h2>VM Admin Tools</h2>
        <div class="muted">Use an active VM session to release a prepared reservation by VM reservation ID.</div>
        <div class="tool-row">
          <label>Prepared VM Res ID<input type="number" [(ngModel)]="preparedReservationId"></label>
          <button type="button" (click)="releasePreparedReservation()" [disabled]="vmBusy() || !preparedReservationId">{{ vmBusy() ? 'Working…' : 'Release Prepared Reservation' }}</button>
        </div>
      </section>
    </section>
  `,
  styles: `
    .stack{display:grid;gap:16px}
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px;display:grid;gap:12px}
    .head{display:flex;justify-content:space-between;gap:12px;align-items:start;flex-wrap:wrap}
    h1,h2{margin:0}
    p,.muted{margin:0;color:var(--muted)}
    .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    label{display:grid;gap:6px;font-weight:600;color:#24334a}
    input,select{height:40px;border:1px solid #c8d2e1;border-radius:8px;padding:0 10px}
    .row{display:flex;align-items:center;gap:8px;align-self:end}
    .row-actions,.tool-row{display:flex;gap:8px;flex-wrap:wrap;align-items:end}
    .error{color:#a42323;background:#fdecec;border:1px solid #f4c0c0;border-radius:8px;padding:8px 10px}
    .ok{color:#0f6f41;background:#e8f7ef;border:1px solid #bce8cf;border-radius:8px;padding:8px 10px}
    button{height:40px;border:0;border-radius:8px;background:#0f4bb8;color:#fff;font-weight:600;cursor:pointer;padding:0 12px}
    .secondary{background:#6b778c}
    @media (max-width: 900px){ .grid{grid-template-columns:1fr} }
  `,
})
export class SettingsCommunicationComponent implements OnInit {
  private readonly api = inject(ApiService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly vmBusy = signal(false);
  readonly error = signal('');
  readonly ok = signal('');
  model: SettingsModel | null = null;
  preparedReservationId: number | null = null;

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  onTlsChanged(v: boolean): void {
    if (!this.model) return;
    this.model.TlsVerify = v ? 1 : 0;
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      const r = await firstValueFrom(this.api.get<{ settings: SettingsModel }>('/settings'));
      this.model = r.settings;
    } catch {
      this.error.set('Failed to load settings.');
    } finally {
      this.loading.set(false);
    }
  }

  async save(): Promise<void> {
    if (!this.model) return;
    this.error.set('');
    this.ok.set('');
    this.saving.set(true);
    try {
      const payload = {
        timeoutSeconds: Number(this.model.TimeoutSeconds),
        retryCount: Number(this.model.RetryCount),
        tlsVerify: this.model.TlsVerify === 1,
      };
      const r = await firstValueFrom(this.api.put<{ settings: SettingsModel }>('/settings', payload));
      this.model = r.settings;
      this.ok.set('Settings saved.');
      window.dispatchEvent(new Event('settings-changed'));
    } catch {
      this.error.set('Failed to save settings.');
    } finally {
      this.saving.set(false);
    }
  }

  async applyDbUpdates(): Promise<void> {
    this.error.set('');
    this.ok.set('');
    try {
      const r = await firstValueFrom(this.api.post<{ message: string }>('/settings/apply-db-updates', {}));
      this.ok.set(r.message);
    } catch {
      this.error.set('Failed to apply DB updates.');
    }
  }

  async releasePreparedReservation(): Promise<void> {
    if (!this.preparedReservationId) return;
    this.error.set('');
    this.ok.set('');
    this.vmBusy.set(true);
    try {
      const result = await firstValueFrom(
        this.api.post<{ ok: boolean; message: string }>('/settings/release-prepared-reservation', { reservationId: Number(this.preparedReservationId) }),
      );
      if (result.ok) this.ok.set(result.message);
      else this.error.set(result.message);
    } catch {
      this.error.set('Release prepared reservation failed.');
    } finally {
      this.vmBusy.set(false);
    }
  }
}
